import { CHAT_MEDIA_TYPES, CHAT_MESSAGE_TYPES, WHATSAPP_TEMPLATE_BUTTON_TYPES } from 'cpool-constants';
import { z } from 'zod';

import { schemaChatTemplateButton } from './schemaChatTemplateButton';
import { schemaId } from '../utils/schema.utils';

const schemaChatMessageQueuedCommonFields = z.object({
    customerIdentity: z.string().min(1),
    messageId: schemaId,
    phoneNumberId: z.string().min(1),
});

export const schemaChatMessageQueuedPayload = z
    .discriminatedUnion('messageType', [
        schemaChatMessageQueuedCommonFields.extend({
            messageType: z.literal(CHAT_MESSAGE_TYPES.TEXT),
            text: z.string().min(1),
        }),
        schemaChatMessageQueuedCommonFields.extend({
            messageType: z.literal(CHAT_MESSAGE_TYPES.TEMPLATE),
            // Suffix-only Meta send instructions for dynamic URL buttons — never the full URL.
            templateButtonParameters: z
                .array(z.object({ index: z.number().int().nonnegative(), value: z.string().min(1) }))
                .optional(),
            // Frozen at command time; array position is the button index.
            templateButtons: z.array(schemaChatTemplateButton).optional(),
            templateHeader: z.string().min(1).optional(),
            // Separate from templateParameters: a POSITIONAL header's {{1}} is a different slot from the body's.
            templateHeaderParameters: z.union([z.array(z.string()), z.record(z.string(), z.string())]).optional(),
            templateLanguage: z.string().min(1),
            templateName: z.string().min(1),
            templateParameters: z.union([z.array(z.string()), z.record(z.string(), z.string())]).optional(),
            // Optional CRM preview body; Meta uses name+params.
            text: z.string().min(1).optional(),
        }),
        // No metaMediaId until Manta upload; caption dropped for audio.
        schemaChatMessageQueuedCommonFields.extend({
            caption: z.string().optional(),
            mediaFileName: z.string().min(1).optional(),
            mediaKey: z.string().min(1),
            // STICKER is inbound-only — never produced by deriveMediaTypeFromMimeType.
            mediaType: z.nativeEnum(CHAT_MEDIA_TYPES).refine((v) => v !== CHAT_MEDIA_TYPES.STICKER, {
                message: 'sticker is inbound-only and cannot be sent',
            }),
            messageType: z.literal(CHAT_MESSAGE_TYPES.MEDIA),
            mimeType: z.string().min(1),
        }),
    ])
    .superRefine((data, ctx) => {
        if (data.messageType !== CHAT_MESSAGE_TYPES.TEMPLATE) return;

        const buttonCount = data.templateButtons?.length ?? 0;
        const seenIndexes = new Set<number>();
        data.templateButtonParameters?.forEach((param, i) => {
            if (param.index >= buttonCount) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `templateButtonParameters[${i}].index (${param.index}) out of range for templateButtons (length ${buttonCount})`,
                    path: ['templateButtonParameters', i, 'index'],
                });
                return;
            }
            if (data.templateButtons?.[param.index]?.type !== WHATSAPP_TEMPLATE_BUTTON_TYPES.URL) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `templateButtonParameters[${i}].index (${param.index}) does not point at a URL button`,
                    path: ['templateButtonParameters', i, 'index'],
                });
            }
            if (seenIndexes.has(param.index)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `templateButtonParameters[${i}].index (${param.index}) duplicates an earlier entry`,
                    path: ['templateButtonParameters', i, 'index'],
                });
            }
            seenIndexes.add(param.index);
        });
    });

export type Schema_ChatMessageQueuedPayload = z.infer<typeof schemaChatMessageQueuedPayload>;
